import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import Navbar from './Navbar';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiImage, FiVideo, FiPlus, FiUpload, FiPlay, FiPause, FiTrash2, FiDownload, FiFolder } = FiIcons;

const MediaLibrary = () => {
  const [mediaFiles, setMediaFiles] = useState([
    {
      id: 1,
      name: 'Welcome Video.mp4',
      type: 'video',
      size: '45.2 MB',
      duration: '2:30',
      uploaded: '2024-01-15',
      thumbnail: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=300&h=200&fit=crop'
    },
    {
      id: 2,
      name: 'Product Showcase.jpg',
      type: 'image',
      size: '2.1 MB',
      uploaded: '2024-01-14',
      thumbnail: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=300&h=200&fit=crop'
    },
    {
      id: 3,
      name: 'Company Logo.png',
      type: 'image',
      size: '256 KB',
      uploaded: '2024-01-13',
      thumbnail: 'https://images.unsplash.com/photo-1599305445671-ac291c95aaa9?w=300&h=200&fit=crop'
    },
    {
      id: 4,
      name: 'Promotional Video.mp4',
      type: 'video',
      size: '78.5 MB',
      duration: '4:15',
      uploaded: '2024-01-12',
      thumbnail: 'https://images.unsplash.com/photo-1551818255-e6e10975bc17?w=300&h=200&fit=crop'
    }
  ]);

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showUploadModal, setShowUploadModal] = useState(false);

  const handleFileSelect = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const handleDeleteSelected = () => {
    if (selectedFiles.length === 0) {
      toast.error('No files selected');
      return;
    }
    
    setMediaFiles(prev => prev.filter(file => !selectedFiles.includes(file.id)));
    setSelectedFiles([]);
    toast.success(`${selectedFiles.length} file(s) deleted`);
  };

  const getFileIcon = (type) => {
    return type === 'video' ? FiVideo : FiImage;
  };

  const getFileTypeColor = (type) => {
    return type === 'video' ? 'from-red-400 to-pink-400' : 'from-blue-400 to-cyan-400';
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Media Library</h1>
              <p className="text-white/70">
                Manage your images, videos, and other content
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {selectedFiles.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-500/20 border border-red-400/30 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors"
                >
                  <SafeIcon icon={FiTrash2} />
                  <span>Delete ({selectedFiles.length})</span>
                </button>
              )}
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                <SafeIcon icon={FiUpload} />
                <span>Upload Media</span>
              </button>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
          >
            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total Files</p>
                  <p className="text-2xl font-bold text-white">{mediaFiles.length}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiFolder} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Images</p>
                  <p className="text-2xl font-bold text-white">
                    {mediaFiles.filter(f => f.type === 'image').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiImage} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Videos</p>
                  <p className="text-2xl font-bold text-white">
                    {mediaFiles.filter(f => f.type === 'video').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-pink-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiVideo} className="text-white text-xl" />
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Storage Used</p>
                  <p className="text-2xl font-bold text-white">126 MB</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-400 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiUpload} className="text-white text-xl" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Media Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-white">All Media</h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/10 text-white/70'
                  }`}
                >
                  <SafeIcon icon={FiImage} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-purple-500/20 text-purple-400' : 'bg-white/10 text-white/70'
                  }`}
                >
                  <SafeIcon icon={FiFolder} />
                </button>
              </div>
            </div>

            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {mediaFiles.map((file) => (
                  <div
                    key={file.id}
                    className={`bg-white/5 border rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300 cursor-pointer ${
                      selectedFiles.includes(file.id) ? 'border-purple-400 ring-2 ring-purple-400/20' : 'border-white/10'
                    }`}
                    onClick={() => handleFileSelect(file.id)}
                  >
                    <div className="aspect-video relative">
                      <img
                        src={file.thumbnail}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-3 left-3">
                        <div className={`w-8 h-8 bg-gradient-to-br ${getFileTypeColor(file.type)} rounded-lg flex items-center justify-center`}>
                          <SafeIcon icon={getFileIcon(file.type)} className="text-white text-sm" />
                        </div>
                      </div>
                      {file.type === 'video' && (
                        <div className="absolute bottom-3 right-3 bg-black/70 px-2 py-1 rounded text-white text-xs">
                          {file.duration}
                        </div>
                      )}
                      {selectedFiles.includes(file.id) && (
                        <div className="absolute inset-0 bg-purple-500/20 border-2 border-purple-400 rounded-lg"></div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-medium text-sm truncate mb-1">{file.name}</h3>
                      <div className="flex justify-between items-center text-xs text-white/70">
                        <span>{file.size}</span>
                        <span>{file.uploaded}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {mediaFiles.map((file) => (
                  <div
                    key={file.id}
                    className={`flex items-center justify-between p-4 rounded-xl hover:bg-white/5 transition-colors cursor-pointer ${
                      selectedFiles.includes(file.id) ? 'bg-purple-500/10 border border-purple-400/30' : 'bg-white/5'
                    }`}
                    onClick={() => handleFileSelect(file.id)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${getFileTypeColor(file.type)} rounded-xl flex items-center justify-center`}>
                        <SafeIcon icon={getFileIcon(file.type)} className="text-white text-xl" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{file.name}</h3>
                        <p className="text-white/70 text-sm">
                          {file.type === 'video' ? `${file.duration} • ` : ''}{file.size} • {file.uploaded}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                        <SafeIcon icon={FiDownload} className="text-white/70" />
                      </button>
                      <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                        <SafeIcon icon={file.type === 'video' ? FiPlay : FiImage} className="text-white/70" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Upload Media</h2>
            
            <div className="border-2 border-dashed border-white/30 rounded-xl p-8 text-center mb-6">
              <SafeIcon icon={FiUpload} className="text-4xl text-white/50 mx-auto mb-4" />
              <p className="text-white/70 mb-2">Drag and drop files here</p>
              <p className="text-white/50 text-sm">or click to browse</p>
              <button className="mt-4 px-4 py-2 bg-purple-500/20 border border-purple-400/30 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors">
                Browse Files
              </button>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  toast.success('Files uploaded successfully!');
                }}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300"
              >
                Upload
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;